import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ApiResponse,
  Elevator,
  CreateElevatorDto,
  UpdateElevatorDto,
  FloorLock,
  FloorMapItem,
  TelemetryData,
  DailyUsageData,
  MonthlyUsageData,
  SelfCheckResult,
  ElevatorSpec,
  PublicEmployeeElevatorData,
  PublicSingleEmployeeElevatorData,
  DeviceCommandLog,
  EmergencyCall,
  EmergencyCallLog,
  ElevatorGalleryItem,
} from '../models';



@Injectable({
  providedIn: 'root'
})
export class ElevatorService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/elevators`;

  getElevators(): Observable<Elevator[]> {
    return this.http.get<ApiResponse<Elevator[]>>(this.baseUrl).pipe(
      map(res => res.data)
    );
  }

  getElevator(id: number): Observable<Elevator> {
    return this.http.get<ApiResponse<Elevator>>(`${this.baseUrl}/${id}`).pipe(
      map(res => res.data)
    );
  }

  createElevator(dto: CreateElevatorDto): Observable<Elevator> {
    return this.http.post<ApiResponse<Elevator>>(this.baseUrl, dto).pipe(
      map(res => res.data)
    );
  }

  updateElevator(id: number, dto: UpdateElevatorDto): Observable<Elevator> {
    return this.http.patch<ApiResponse<Elevator>>(`${this.baseUrl}/${id}`, dto).pipe(
      map(res => res.data)
    );
  }

  deleteElevator(id: number): Observable<{ success: boolean; message: string }> {
    return this.http.delete<ApiResponse<{ success: boolean; message: string }>>(`${this.baseUrl}/${id}`).pipe(
      map(res => res.data)
    );
  }

  updateFloorMap(id: number, floorMap: FloorMapItem[]): Observable<Elevator> {
    return this.http.put<ApiResponse<Elevator>>(`${this.baseUrl}/${id}/floor-map`, { floorMap }).pipe(
      map(res => res.data)
    );
  }

  getDefaultFloorMap(id: number): Observable<FloorMapItem[]> {
    return this.http.get<ApiResponse<FloorMapItem[]>>(`${this.baseUrl}/${id}/default-floor-map`).pipe(
      map(res => res.data)
    );
  }

  getTelemetry(id: number, limit = 40): Observable<TelemetryData[]> {
    return this.http.get<ApiResponse<TelemetryData[]>>(`${environment.apiUrl}/telemetry/elevator/${id}?limit=${limit}`).pipe(
      map(res => res.data)
    );
  }

  sendControlCommand(id: number, targetFloor?: number, command?: string, value?: string): Observable<Elevator> {
    const body: Record<string, any> = { command };
    if (targetFloor !== undefined && targetFloor !== null) {
      body['targetFloor'] = targetFloor;
    }
    if (value !== undefined && value !== null) {
      body['value'] = value;
    }
    return this.http.post<ApiResponse<Elevator>>(`${this.baseUrl}/${id}/control`, body).pipe(
      map(res => res.data)
    );
  }

  sendDeviceCommand(id: number, command: string, value?: string): Observable<{ success: boolean; message: string; payload: { command: string; value: string } }> {
    return this.http.post<ApiResponse<{ success: boolean; message: string; payload: { command: string; value: string } }>>(
      `${this.baseUrl}/${id}/device-command`,
      { command, value: value ?? '' }
    ).pipe(
      map(res => res.data)
    );
  }

  startContactStream(id: number, duration: number = 180): Observable<{ success: boolean; message: string; duration: number; interval: number }> {
    return this.http.post<ApiResponse<{ success: boolean; message: string; duration: number; interval: number }>>(
      `${this.baseUrl}/${id}/contact-signals/fast-stream`,
      { duration }
    ).pipe(
      map(res => res.data)
    );
  }

  stopContactStream(id: number): Observable<{ success: boolean; message: string }> {
    return this.http.post<ApiResponse<{ success: boolean; message: string }>>(
      `${this.baseUrl}/${id}/contact-signals/stop-stream`,
      {}
    ).pipe(
      map(res => res.data)
    );
  }

  getContactSignals(id: number): Observable<any> {
    return this.http.get<ApiResponse<any>>(
      `${this.baseUrl}/${id}/contact-signals`
    ).pipe(
      map(res => res.data)
    );
  }

  getCommandLogs(
    id: number,
    params?: {
      startDate?: string;
      endDate?: string;
      search?: string;
      command?: string;
      status?: string;
      limit?: number;
    }
  ): Observable<DeviceCommandLog[]> {
    const queryParams: Record<string, string> = {};
    if (params) {
      if (params.startDate) queryParams['startDate'] = params.startDate;
      if (params.endDate) queryParams['endDate'] = params.endDate;
      if (params.search) queryParams['search'] = params.search;
      if (params.command) queryParams['command'] = params.command;
      if (params.status) queryParams['status'] = params.status;
      if (params.limit) queryParams['limit'] = String(params.limit);
    }
    return this.http
      .get<ApiResponse<DeviceCommandLog[]>>(`${this.baseUrl}/${id}/command-logs`, {
        params: queryParams,
      })
      .pipe(map(res => res.data));
  }

  getComparison(id: number): Observable<Record<string, unknown>> {
    return this.http.get<ApiResponse<Record<string, unknown>>>(`${environment.apiUrl}/telemetry/elevator/${id}/comparison`).pipe(
      map(res => res.data)
    );
  }

  getWeeklyEnvironment(id: number): Observable<any> {
    return this.http.get<ApiResponse<any>>(`${environment.apiUrl}/telemetry/elevator/${id}/environment-weekly`).pipe(
      map(res => res.data)
    );
  }

  getGridTrend(id: number): Observable<any> {
    return this.http.get<ApiResponse<any>>(`${environment.apiUrl}/telemetry/elevator/${id}/grid-trend`).pipe(
      map(res => res.data)
    );
  }

  updateFloorLocks(id: number, floorLocks: FloorLock[]): Observable<Elevator> {
    return this.http.post<ApiResponse<Elevator>>(`${this.baseUrl}/${id}/floor-locks`, floorLocks).pipe(
      map(res => res.data)
    );
  }

  getDailyUsage(): Observable<DailyUsageData[]> {
    return this.http.get<ApiResponse<DailyUsageData[]>>(`${environment.apiUrl}/telemetry/usage/daily`).pipe(
      map(res => res.data)
    );
  }

  getMonthlyUsage(): Observable<MonthlyUsageData[]> {
    return this.http.get<ApiResponse<MonthlyUsageData[]>>(`${environment.apiUrl}/telemetry/usage/monthly`).pipe(
      map(res => res.data)
    );
  }

  getSelfChecks(id: number, yyyymm: string, pageNo = '1', numOfRows = '10000'): Observable<SelfCheckResult> {
    return this.http.get<ApiResponse<SelfCheckResult>>(
      `${this.baseUrl}/${id}/self-checks?yyyymm=${yyyymm}&pageNo=${pageNo}&numOfRows=${numOfRows}`
    ).pipe(
      map(res => res.data)
    );
  }

  getSpecs(id: number): Observable<ElevatorSpec> {
    return this.http.get<ApiResponse<ElevatorSpec>>(`${this.baseUrl}/${id}/specs`).pipe(
      map(res => res.data)
    );
  }

  getPublicReportDetail(id: number, yyyymm: string): Observable<any> {
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/${id}/public-detail?yyyymm=${yyyymm}`).pipe(
      map(res => res.data)
    );
  }

  getPublicReportPdfUrl(id: number, yyyymm: string): string {
    return `${environment.apiUrl}/elevators/${id}/public-report-pdf?yyyymm=${yyyymm}`;
  }

  getPublicEmployeeElevators(yyyymm?: string): Observable<PublicEmployeeElevatorData> {
    const query = yyyymm ? `?yyyymm=${yyyymm}` : '';
    return this.http.get<ApiResponse<PublicEmployeeElevatorData>>(`${environment.apiUrl}/public/employee-elevators${query}`).pipe(
      map(res => res.data)
    );
  }

  getPublicSingleEmployeeElevators(employeeId: number, yyyymm?: string): Observable<PublicSingleEmployeeElevatorData> {
    const query = yyyymm ? `/${yyyymm}` : '';
    return this.http.get<ApiResponse<PublicSingleEmployeeElevatorData>>(`${environment.apiUrl}/public/employee-elevators/${employeeId}${query}`).pipe(
      map(res => res.data)
    );
  }

  sendElevatorMonthlyReport(id: number, yyyymm: string): Observable<any> {
    const formattedYyyymm = yyyymm.replace('-', '');
    return this.http.post<ApiResponse<any>>(
      `${environment.apiUrl}/notification/send-employee-monthly-reports?elevatorId=${id}&yyyymm=${formattedYyyymm}`,
      {}
    ).pipe(
      map(res => res.data)
    );
  }

  updateModuleConfig(id: number, moduleConfig: Record<string, boolean>): Observable<Elevator> {
    return this.http.put<ApiResponse<Elevator>>(`${this.baseUrl}/${id}/module-config`, { moduleConfig }).pipe(
      map(res => res.data)
    );
  }

  getGalleryList(elevatorId: number): Observable<ElevatorGalleryItem[]> {
    return this.http
      .get<ApiResponse<ElevatorGalleryItem[]>>(`${this.baseUrl}/${elevatorId}/gallery`)
      .pipe(map(res => res.data));
  }

  uploadGalleryImages(
    elevatorId: number,
    files: FileList | File[],
  ): Observable<ElevatorGalleryItem[]> {
    const formData = new FormData();
    Array.from(files).forEach(file => formData.append('files', file));
    return this.http
      .post<ApiResponse<ElevatorGalleryItem[]>>(
        `${this.baseUrl}/${elevatorId}/gallery`,
        formData,
      )
      .pipe(map(res => res.data));
  }

  deleteGalleryImage(
    elevatorId: number,
    galleryId: number,
  ): Observable<{
    success: boolean;
    message: string;
    galleries: ElevatorGalleryItem[];
  }> {
    return this.http
      .delete<
        ApiResponse<{
          success: boolean;
          message: string;
          galleries: ElevatorGalleryItem[];
        }>
      >(`${this.baseUrl}/${elevatorId}/gallery/${galleryId}`)
      .pipe(map(res => res.data));
  }

  getGalleryImageUrl(
    elevatorId: number,
    galleryId: number,
    download = false,
  ): string {
    const base = `${this.baseUrl}/${elevatorId}/gallery/${galleryId}/file`;
    return download ? `${base}?download=true` : base;
  }

  getEmergencyCalls(elevatorId?: number): Observable<EmergencyCall[]> {
    const url = elevatorId
      ? `${environment.apiUrl}/emergency-calls?elevatorId=${elevatorId}`
      : `${environment.apiUrl}/emergency-calls`;
    return this.http.get<ApiResponse<EmergencyCall[]>>(url).pipe(
      map(res => res.data)
    );
  }

  getEmergencyCallLogs(
    elevatorId?: number,
    sessionId?: string,
    callId?: number,
  ): Observable<EmergencyCallLog[]> {
    const params: string[] = [];
    if (elevatorId) params.push(`elevatorId=${elevatorId}`);
    if (sessionId) params.push(`sessionId=${encodeURIComponent(sessionId)}`);
    if (callId) params.push(`callId=${callId}`);

    const query = params.length > 0 ? `?${params.join('&')}` : '';
    return this.http
      .get<ApiResponse<EmergencyCallLog[]>>(
        `${environment.apiUrl}/emergency-calls/logs${query}`,
      )
      .pipe(map(res => res.data));
  }

  updateEmergencyCallCategory(
    id: number,
    category: 'REAL' | 'MISTAKE',
  ): Observable<EmergencyCall> {
    return this.http
      .patch<ApiResponse<EmergencyCall>>(
        `${environment.apiUrl}/emergency-calls/${id}/category`,
        { category },
      )
      .pipe(map(res => res.data));
  }
}



